// @dynamic-i18n-keys
// navigation.route.var.*
import { useRef, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { Route, RouteComponentProps, matchPath } from 'react-router';
import { useTranslation } from 'react-i18next';
import { setRef } from '@wix/da-ds/pkg/utils/refHelpers';
import UnderlinedTabs from '@wix/da-ds/pkg/Tabs/dsPresets/UnderlinedTabs';
import { Nav } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import classnames from 'classnames';
import HiddenIcon from '@wix/da-ds/pkg/Icons/Hidden';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { Route as RouteDefinition } from '../../../../types/route';
import NavLink from './NavLink';

import s from './SubNavBarMobile.scss';

export interface SubNavBarProps {
  routes: RouteDefinition[];
  isSticky: boolean;
  biClickProfileNavBar: (linkName: string) => void;
}

function SubNavBarMobileInner(props: SubNavBarProps & RouteComponentProps<any>) {
  const { t } = useTranslation();
  const navRef = useRef<HTMLElement | null>(null);
  const navListRef = useRef<HTMLElement | null>(null);
  const activeRouteRef = useRef<any>(null);

  const scrollToActive = useCallback(() => {
    if (!navRef.current || !navListRef.current || !activeRouteRef.current) {
      return;
    }
    navRef.current.scrollLeft =
      activeRouteRef.current.offsetLeft - navRef.current.getBoundingClientRect().width / 2;
  }, []);

  useEffect(() => {
    scrollToActive();
  }, [scrollToActive]);

  const { isSticky, routes, biClickProfileNavBar } = props;
  return (
    <Nav
      className={classnames(s['navbar'], s['design-update'], isSticky && s['sticky'])}
      ref={navRef as any}
    >
      <VisuallyHidden>
        <Heading>{t('navigation.subnavigation.heading')}</Heading>
      </VisuallyHidden>

      <div className={s['nav-list']} ref={navListRef as any}>
        <Route
          key="nav-user"
          path="/:username"
          render={({ match, location }) => (
            <UnderlinedTabs
              className={s['nav-list']}
              activeItemRef={node => setRef(activeRouteRef, node)}
              activeIndex={routes.findIndex(
                route =>
                  !!matchPath(location.pathname, {
                    path: route.path,
                    exact: route.exact,
                  })
              )}
              items={routes.map(
                (route, index) =>
                  ({
                    prefix: route.isPrivate && (
                      <HiddenIcon
                        size={IconSize.TINY}
                        aria-label={t('navigation.route.a11y.private')}
                      />
                    ),
                    label: (
                      <NavLink
                        key={route.key}
                        to={(route.path || '')
                          .replace(/:username/, match.params.username)
                          .replace(/\/:[a-z0-9_()\\+]+\?/gi, '')}
                        onClick={() => biClickProfileNavBar(route.key)}
                      >
                        {t(`navigation.route.var.${route.key}`)}
                      </NavLink>
                    ),
                    labelExtras: route.isBeta && <span className={s['beta']}>beta</span>,
                    index,
                  }) as any
              )}
            />
          )}
        />
      </div>
    </Nav>
  );
}

export default withRouter(SubNavBarMobileInner);
